import { useEffect, useMemo, useState } from 'react'
import type { FormEvent } from 'react'

import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardHeader from '@mui/material/CardHeader'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Divider from '@mui/material/Divider'
import Grid from '@mui/material/Grid2'
import MenuItem from '@mui/material/MenuItem'
import Typography from '@mui/material/Typography'
import {
  createColumnHelper,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable
} from '@tanstack/react-table'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { toast } from 'react-toastify'

import type { PlanProps } from '@server/domain/billing'
import type { UserRole } from '@server/domain/user'

import CustomAvatar from '@core/components/mui/avatar'
import CustomChip from '@core/components/mui/chip'
import CustomInputHorizontal from '@core/components/custom-inputs/horizontal'
import CustomTextField from '@core/components/mui/text-field'
import type { ThemeColor } from '@core/types'
import DialogCloseButton from '@components/dialogs/dialog-close-button'
import { MenuRow } from '@components/menu-row'
import { TableDefault } from '@components/table-default'

import { useTRPC } from '@/lib/trpc'
import { requireAdmin } from '@/lib/route-guards'
import { formatBrl, formatDateTime } from '@/lib/due-diligence'

export const Route = createFileRoute('/_dashboard/admin/users')({
  beforeLoad: requireAdmin,
  component: Members
})

const ROLES: Record<UserRole, { label: string; description: string; color: ThemeColor }> = {
  admin: { label: 'Administrador', description: 'Acesso total, inclusive à operação interna', color: 'error' },
  analyst: { label: 'Analista', description: 'Cria diligências e dispara as buscas', color: 'primary' },
  viewer: { label: 'Leitor', description: 'Só consulta — não edita nada', color: 'info' },
  auditor: { label: 'Auditor', description: 'Vê o log de auditoria e os dados de retenção', color: 'secondary' }
}

/** What `members.list` sends: the account, its plan and whether it is active. */
type MemberWithPlan = {
  id: string
  name: string
  email: string
  username: string | null
  role: UserRole
  /** `OAB/SP 123.456`, ou nulo para quem não é advogado. */
  oab: string | null
  /** Se a pessoa pode assinar parecer — OAB registrada e conta ativa. */
  canSignOpinion: boolean
  active: boolean
  createdAt: Date
  plan: PlanProps | null
  subscriptionStatus: string | null
}

const columnHelper = createColumnHelper<MemberWithPlan>()

/** A readable temporary password: no characters that get confused out loud. */
const generatePassword = () => {
  const alphabet = 'abcdefghijkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789'
  const values = crypto.getRandomValues(new Uint32Array(14))

  return Array.from(values, value => alphabet[value % alphabet.length]).join('')
}

function Members() {
  const trpc = useTRPC()
  const queryClient = useQueryClient()

  const [creating, setCreating] = useState(false)
  const [editing, setEditing] = useState<MemberWithPlan | null>(null)

  const { data: members = [], isPending } = useQuery(trpc.members.list.queryOptions())
  const { data: plans = [] } = useQuery(trpc.billing.plans.queryOptions())

  const setActive = useMutation(
    trpc.members.setActive.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: trpc.members.list.queryKey() })
        toast.success('Situação do usuário atualizada.')
      },
      onError: error => toast.error(error.message)
    })
  )

  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        header: 'Usuário',
        cell: ({ row }) => (
          <div className='flex items-center gap-3'>
            <CustomAvatar skin='light' color={ROLES[row.original.role].color} size={34}>
              {row.original.name
                .split(' ')
                .map(part => part[0])
                .slice(0, 2)
                .join('')}
            </CustomAvatar>
            <div className='min-is-0'>
              <Typography className='font-medium truncate'>{row.original.name}</Typography>
              <Typography variant='caption' color='text.disabled' className='truncate'>
                {row.original.email}
              </Typography>
            </div>
          </div>
        )
      }),
      columnHelper.accessor('role', {
        header: 'Papel',
        cell: ({ getValue }) => (
          <CustomChip
            round='true'
            size='small'
            variant='tonal'
            color={ROLES[getValue()].color}
            label={ROLES[getValue()].label}
          />
        )
      }),
      columnHelper.accessor(row => row.plan?.name ?? '', {
        id: 'plan',
        header: 'Plano',
        cell: ({ row }) =>
          row.original.plan ? (
            <div>
              <Typography variant='body2' className='font-medium'>
                {row.original.plan.name}
              </Typography>
              {row.original.plan.monthlyPriceBrl > 0 && (
                <Typography variant='caption' color='text.disabled'>
                  {formatBrl(row.original.plan.monthlyPriceBrl)}/mês
                </Typography>
              )}
            </div>
          ) : (
            <Typography variant='body2' color='text.disabled' className='italic'>
              sem plano
            </Typography>
          )
      }),
      columnHelper.accessor('createdAt', {
        header: 'Cadastrado em',
        cell: ({ getValue }) => (
          <Typography variant='body2' color='text.secondary'>
            {formatDateTime(getValue())}
          </Typography>
        )
      }),
      columnHelper.accessor('active', {
        header: 'Situação',
        cell: ({ getValue }) => (
          <CustomChip
            round='true'
            size='small'
            variant='tonal'
            color={getValue() ? 'success' : 'secondary'}
            label={getValue() ? 'Ativo' : 'Inativo'}
          />
        )
      }),
      columnHelper.display({
        id: 'actions',
        header: '',
        cell: ({ row }) => (
          <div className='flex items-center justify-end'>
            <Button size='small' className='min-is-0 p-2' onClick={() => setEditing(row.original)}>
              <i className='tabler-edit' />
            </Button>
            <MenuRow
              options={[
                {
                  label: row.original.active ? 'Desativar acesso' : 'Reativar acesso',
                  onClick: () => setActive.mutate({ id: row.original.id, active: !row.original.active })
                }
              ]}
            />
          </div>
        )
      })
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  const table = useReactTable({
    data: members,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: { pagination: { pageSize: 10 } }
  })

  const activeCount = members.filter(member => member.active).length

  return (
    <>
      <Card>
        <CardHeader
          title='Usuários'
          subheader={`${members.length} cadastrados · ${activeCount} ativos`}
          action={
            <Button variant='contained' startIcon={<i className='tabler-user-plus' />} onClick={() => setCreating(true)}>
              Novo usuário
            </Button>
          }
        />
        <Divider />
        <div className='overflow-x-auto'>
          <TableDefault
            table={table}
            withPagination
            emptyMessage={isPending ? 'Carregando…' : 'Nenhum usuário cadastrado.'}
          />
        </div>
      </Card>

      <MemberDialog open={creating} plans={plans} onClose={() => setCreating(false)} />
      <MemberDialog open={Boolean(editing)} member={editing} plans={plans} onClose={() => setEditing(null)} />
    </>
  )
}

type MemberDialogProps = {
  open: boolean
  member?: MemberWithPlan | null
  plans: PlanProps[]
  onClose: () => void
}

const BLANK_FORM = {
  name: '',
  email: '',
  password: '',
  role: 'analyst' as UserRole,
  planCode: '',
  oabNumber: '',
  oabState: ''
}

/**
 * One form for creating and for editing.
 *
 * When editing, the password is optional — blank keeps the current one, filled
 * in makes it a reset. The plan is only touched when it changes, so no charge
 * is raised for nothing.
 */
function MemberDialog({ open, member, plans, onClose }: MemberDialogProps) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()

  const editingMode = Boolean(member)

  /**
   * Under `method: 'email-code'` an account has no password, so asking an
   * administrator to invent one would be asking for a secret nobody uses.
   * The server is the one that decides — this only mirrors the answer.
   */
  const { data: authSettings } = useQuery(trpc.auth.settings.queryOptions())
  const usesPassword = authSettings?.method === 'password'

  const [form, setForm] = useState(BLANK_FORM)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!open) return

    setError('')
    setForm(
      member
        ? {
            name: member.name,
            email: member.email,
            password: '',
            role: member.role,
            planCode: member.plan?.code ?? '',
            oabNumber: member.oab?.replace(/^OAB\/\w{2}\s*/, '') ?? '',
            oabState: member.oab?.slice(4, 6) ?? ''
          }
        : BLANK_FORM
    )
  }, [open, member])

  const invalidate = () => queryClient.invalidateQueries({ queryKey: trpc.members.list.queryKey() })

  const create = useMutation(
    trpc.members.create.mutationOptions({
      onSuccess: () => {
        void invalidate()
        toast.success('Usuário criado.')
        onClose()
      },
      onError: mutationError => setError(mutationError.message)
    })
  )

  const update = useMutation(trpc.members.update.mutationOptions({ onError: mutationError => setError(mutationError.message) }))
  const changePlan = useMutation(
    trpc.members.changePlan.mutationOptions({ onError: mutationError => setError(mutationError.message) })
  )

  const saving = create.isPending || update.isPending || changePlan.isPending

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    setError('')

    if (usesPassword && !editingMode && form.password.length < 10) {
      setError('A senha inicial precisa de ao menos 10 caracteres.')

      return
    }

    if (usesPassword && editingMode && form.password && form.password.length < 10) {
      setError('Para trocar a senha, use ao menos 10 caracteres — ou deixe em branco para manter.')

      return
    }

    if (!editingMode) {
      create.mutate({ ...form, password: form.password || undefined })

      return
    }

    if (!member) return

    await update.mutateAsync({
      id: member.id,
      name: form.name,
      email: form.email,
      role: form.role,
      // Enviados sempre, porque vazio aqui quer dizer "apague a inscrição" — e
      // apagar tira da pessoa o direito de assinar parecer.
      oabNumber: form.oabNumber,
      oabState: form.oabState,
      ...(form.password ? { newPassword: form.password } : {})
    })

    if (form.planCode && form.planCode !== (member.plan?.code ?? '')) {
      await changePlan.mutateAsync({ userId: member.id, planCode: form.planCode, method: 'card' })
    }

    void invalidate()
    toast.success('Usuário atualizado.')
    onClose()
  }

  return (
    <Dialog fullWidth maxWidth='sm' open={open} onClose={onClose} closeAfterTransition={false} scroll='body'>
      <DialogCloseButton onClick={onClose} disableRipple>
        <i className='tabler-x' />
      </DialogCloseButton>
      <DialogTitle className='flex flex-col gap-1 sm:pbs-10 sm:pli-10'>
        {editingMode ? `Editar ${member?.name}` : 'Novo usuário'}
        <Typography component='span' variant='body2' color='text.secondary'>
          O papel define o que a pessoa pode fazer; o plano, quanto ela pode consumir.
        </Typography>
      </DialogTitle>
      <form noValidate onSubmit={submit}>
        <DialogContent className='sm:pli-10'>
          <Grid container spacing={5}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <CustomTextField
                fullWidth
                required
                label='Nome completo'
                placeholder='Maria Silva'
                value={form.name}
                onChange={event => setForm(current => ({ ...current, name: event.target.value }))}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <CustomTextField
                fullWidth
                required
                type='email'
                label='E-mail'
                placeholder='maria@empresa.com'
                value={form.email}
                onChange={event => setForm(current => ({ ...current, email: event.target.value }))}
              />
            </Grid>
            {usesPassword && (
              <Grid size={12}>
                <div className='flex items-end gap-3'>
                  <CustomTextField
                    fullWidth
                    label={editingMode ? 'Nova senha (opcional)' : 'Senha inicial'}
                    placeholder={editingMode ? 'deixe em branco para manter' : 'mínimo de 10 caracteres'}
                    helperText={
                      editingMode
                        ? 'Em branco mantém a senha atual.'
                        : 'A pessoa pode trocar depois nas configurações da conta.'
                    }
                    value={form.password}
                    onChange={event => setForm(current => ({ ...current, password: event.target.value }))}
                  />
                  <Button
                    variant='tonal'
                    color='secondary'
                    className='mbe-6'
                    onClick={() => setForm(current => ({ ...current, password: generatePassword() }))}
                  >
                    Gerar
                  </Button>
                </div>
              </Grid>
            )}

            {/*
              A inscrição é atributo, não papel. Um advogado do escritório
              costuma ser analista, e transformar "pode assinar" num papel
              obrigaria a dar poder de sistema a quem só precisa assinar.
            */}
            <Grid size={{ xs: 12, sm: 8 }}>
              <CustomTextField
                fullWidth
                label='Inscrição na OAB'
                placeholder='123.456'
                helperText='Sem inscrição, a pessoa não assina parecer.'
                value={form.oabNumber}
                onChange={event => setForm(current => ({ ...current, oabNumber: event.target.value }))}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 4 }}>
              <CustomTextField
                fullWidth
                label='Seccional'
                placeholder='SP'
                value={form.oabState}
                onChange={event =>
                  setForm(current => ({ ...current, oabState: event.target.value.toUpperCase().slice(0, 2) }))
                }
              />
            </Grid>

            <Grid size={12}>
              <Typography variant='body2' color='text.secondary' className='mbe-3'>
                Papel
              </Typography>
              <Grid container spacing={4}>
                {(Object.keys(ROLES) as UserRole[]).map(key => (
                  <CustomInputHorizontal
                    key={key}
                    type='radio'
                    name='member-role'
                    selected={form.role}
                    gridProps={{ size: { xs: 12, sm: 6 } }}
                    handleChange={value =>
                      setForm(current => ({
                        ...current,
                        role: (typeof value === 'string' ? value : value.target.value) as UserRole
                      }))
                    }
                    data={{ value: key, title: ROLES[key].label, content: ROLES[key].description }}
                  />
                ))}
              </Grid>
            </Grid>

            <Grid size={12}>
              <CustomTextField
                select
                fullWidth
                label='Plano'
                helperText={
                  editingMode
                    ? 'Trocar o plano gera cobrança imediata da mensalidade do ciclo.'
                    : 'Sem plano, a pessoa escolhe um no primeiro acesso.'
                }
                value={form.planCode}
                slotProps={{ select: { displayEmpty: true } }}
                onChange={event => setForm(current => ({ ...current, planCode: event.target.value }))}
              >
                <MenuItem value=''>— Sem plano —</MenuItem>
                {plans.map(plan => (
                  <MenuItem key={plan.code} value={plan.code}>
                    {plan.name}
                    {plan.monthlyPriceBrl > 0 ? ` — ${formatBrl(plan.monthlyPriceBrl)}/mês` : ''}
                  </MenuItem>
                ))}
              </CustomTextField>
            </Grid>

            {error && (
              <Grid size={12}>
                <Alert severity='error'>{error}</Alert>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions className='justify-end sm:pbe-10 sm:pli-10'>
          <Button variant='tonal' color='secondary' type='button' onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button
            variant='contained'
            type='submit'
            disabled={saving}
            startIcon={<i className={saving ? 'tabler-loader-2 animate-spin' : 'tabler-check'} />}
          >
            {editingMode ? 'Salvar alterações' : 'Criar usuário'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
